import { createElement } from '../../../core/dom/helpers.js';
import { createReorderControls } from '../../../core/dom/reorder-controls.js';
import { createFieldRow } from './field-row.js';

/**
 * @typedef {import('../../../contracts/forms.js').FormBuilderFieldset} FormBuilderFieldset
 * @typedef {import('../../../contracts/forms.js').FormBuilderField} FormBuilderField
 */

/** A fresh blank field (default type input_text). @returns {FormBuilderField} */
function blankField() {
  return { cssClass: 'input_text', label: '', title: '', required: false, multiple: false, options: [] };
}

/**
 * ONE fieldset card — a name input plus an ordered list of field rows with add / remove /
 * drag-and-keyboard reorder. The middle layer between the driver (which owns the fieldset list)
 * and `field-row.js` (which owns a single field). Mirrors the driver↔card relationship of
 * `videos-driver.js`↔`video-card.js`, one level down: this card owns its OWN field keys (`fd-`)
 * and field-row DOM, re-resolving the key on each callback via `fieldKeys.indexOf(key)`.
 *
 * Field-level drag is scoped WITHIN this fieldset (a field can't be dragged to another fieldset
 * in v1); the keyboard Move buttons are the accessible path.
 *
 * The card reports changes upward by KEY: the driver patches the fieldset by its `fs-` key.
 * - `onNameChange(fsKey, name)`
 * - `onFieldsChange(fsKey, fields)` — the full, current keyless `FormBuilderField[]`
 *
 * @param {Object} args
 * @param {string} args.key - Stable fieldset key the callbacks echo back.
 * @param {FormBuilderFieldset} args.value
 * @param {(key: string, name: string) => void} args.onNameChange
 * @param {(key: string, fields: FormBuilderField[]) => void} args.onFieldsChange
 * @param {(key: string) => void} args.onRemove
 * @param {(key: string) => void} args.onMoveUp
 * @param {(key: string) => void} args.onMoveDown
 * @returns {{ el: HTMLElement, handle: HTMLElement, setDisabled: (d: boolean) => void, setMoveState: (canUp: boolean, canDown: boolean) => void, focusMove: (preferUp: boolean) => void, refreshValidity: () => void, destroy: () => void }}
 */
export function createFieldsetCard({ key, value, onNameChange, onFieldsChange, onRemove, onMoveUp, onMoveDown }) {
  let disabled = false;

  /** @type {FormBuilderField[]} Live fields (source of truth is the driver; this is the local mirror). */
  let fields = (value.fields ?? []).map((f) => ({ ...f }));

  /** Monotonic field-key source — field keys are this card's private state. */
  let fieldKeySeq = 0;
  const nextFieldKey = () => `fd-${key}-${++fieldKeySeq}`;
  /** @type {string[]} Field keys, lockstep with `fields`. */
  let fieldKeys = fields.map(() => nextFieldKey());
  /** @type {ReturnType<typeof createFieldRow>[]} Live field-row handles (destroyed before each rebuild). */
  let rowHandles = [];

  const card = createElement('div', { class: 'fb-fieldset', role: 'group' });

  // Header: reorder controls + name input + remove.
  const header = createElement('div', { class: 'fb-fieldset__header' });
  const reorder = createReorderControls({ key, ariaNoun: 'fieldset', orientation: 'row', onMoveUp, onMoveDown });
  // withHandle defaults true → the handle is always present (the card's contract stays non-null).
  const handle = /** @type {HTMLElement} */ (reorder.handle);

  const nameId = `fbfieldset-name-${key}`;
  const nameLabel = createElement('label', { class: 'fb-fieldset__name-label', for: nameId }, ['Fieldset name']);
  const nameInput = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'text', id: nameId, class: 'fb-fieldset__name', placeholder: 'Fieldset name',
  }));
  nameInput.value = value.name ?? '';
  nameInput.addEventListener('input', () => {
    if (disabled) return;
    onNameChange(key, nameInput.value);
    updateAriaLabel();
  });

  const removeBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button', class: 'fb-fieldset__remove', 'aria-label': 'Remove fieldset',
  }, ['×']));
  removeBtn.addEventListener('click', () => { if (!disabled) onRemove(key); });

  header.appendChild(reorder.el);
  header.appendChild(nameLabel);
  header.appendChild(nameInput);
  header.appendChild(removeBtn);

  // Field list + add-field button.
  const list = createElement('div', { class: 'fb-fieldset__fields' });
  const addFieldBtn = /** @type {HTMLButtonElement} */ (createElement('button', {
    type: 'button', class: 'fb-fieldset__add-field btn btn-secondary',
  }, ['Add field']));
  addFieldBtn.addEventListener('click', () => {
    if (disabled) return;
    fields = [...fields, blankField()];
    fieldKeys = [...fieldKeys, nextFieldKey()];
    onFieldsChange(key, fields);
    renderFields();
  });

  card.appendChild(header);
  card.appendChild(list);
  card.appendChild(addFieldBtn);

  /** @type {string | null} */
  let draggedKey = null;

  /** Patch the field at the index resolved from `fdKey`. @param {string} fdKey @param {Partial<FormBuilderField>} patch */
  function patchField(fdKey, patch) {
    const i = fieldKeys.indexOf(fdKey);
    if (i === -1) return;
    fields = fields.map((f, j) => (j === i ? { ...f, ...patch } : f));
    onFieldsChange(key, fields);
  }

  /** @param {number} i */
  function removeFieldAt(i) {
    if (i < 0 || i >= fields.length) return;
    fields = [...fields.slice(0, i), ...fields.slice(i + 1)];
    fieldKeys = [...fieldKeys.slice(0, i), ...fieldKeys.slice(i + 1)];
    onFieldsChange(key, fields);
    renderFields();
  }

  /**
   * @param {number} from @param {number} to @param {{ focus?: boolean }} [opts]
   * `opts.focus` (set ONLY by the field Move buttons, never by drag) re-homes keyboard focus onto
   * the moved field-row's Move button after the rebuild — a drag must NOT steal focus to a button.
   */
  function moveField(from, to, opts = {}) {
    if (from === to || from < 0 || to < 0 || from >= fields.length || to >= fields.length) return;
    const movedKey = fieldKeys[from];
    const movingUp = to < from;
    const nf = [...fields]; const nk = [...fieldKeys];
    const [mf] = nf.splice(from, 1); const [mk] = nk.splice(from, 1);
    nf.splice(to, 0, mf); nk.splice(to, 0, mk);
    fields = nf; fieldKeys = nk;
    onFieldsChange(key, fields);
    renderFields();
    if (opts.focus) rowHandles[fieldKeys.indexOf(movedKey)]?.focusMove?.(movingUp);
  }

  /** Build the field-row at index `idx` with drag SPLIT (handle = origin, root = drop target). @param {number} idx */
  function makeFieldRow(idx) {
    const fr = createFieldRow({
      key: fieldKeys[idx],
      value: fields[idx],
      onChange: (fdKey, patch) => patchField(fdKey, patch),
      onRemove: (fdKey) => { if (!disabled) removeFieldAt(fieldKeys.indexOf(fdKey)); },
      onMoveUp: (fdKey) => { if (disabled) return; const i = fieldKeys.indexOf(fdKey); moveField(i, i - 1, { focus: true }); },
      onMoveDown: (fdKey) => { if (disabled) return; const i = fieldKeys.indexOf(fdKey); moveField(i, i + 1, { focus: true }); },
    });
    rowHandles.push(fr);
    const root = fr.el;
    root.dataset.fieldIdx = String(idx);

    const frHandle = fr.handle;
    frHandle.addEventListener('dragstart', (e) => {
      if (disabled) { e.preventDefault(); return; }
      draggedKey = fieldKeys[idx];
      root.classList.add('fb-field--dragging');
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    });
    frHandle.addEventListener('dragend', () => {
      draggedKey = null;
      root.classList.remove('fb-field--dragging');
      list.querySelectorAll('.fb-field--over').forEach((el) => el.classList.remove('fb-field--over'));
    });
    root.addEventListener('dragover', (e) => {
      if (draggedKey === null) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      root.classList.add('fb-field--over');
    });
    root.addEventListener('dragleave', () => root.classList.remove('fb-field--over'));
    root.addEventListener('drop', (e) => {
      e.preventDefault();
      root.classList.remove('fb-field--over');
      if (draggedKey === null) return;
      moveField(fieldKeys.indexOf(draggedKey), parseInt(root.dataset.fieldIdx ?? '-1', 10));
    });
    return root;
  }

  function renderFields() {
    if (rowHandles.length) { for (const h of rowHandles) h.destroy(); rowHandles = []; }
    list.textContent = '';
    fields.forEach((_f, i) => list.appendChild(makeFieldRow(i)));
    const last = rowHandles.length - 1;
    rowHandles.forEach((h, i) => {
      h.setMoveState(i > 0, i < last);
      h.setDisabled(disabled);
    });
  }

  function updateAriaLabel() {
    card.setAttribute('aria-label', `Fieldset: ${nameInput.value.trim() || 'Untitled'}`);
  }

  renderFields();
  updateAriaLabel();

  return {
    el: card,
    handle,
    /** @param {boolean} d */
    setDisabled(d) {
      disabled = d;
      nameInput.disabled = d;
      removeBtn.disabled = d;
      addFieldBtn.disabled = d;
      reorder.setDisabled(d); // fieldset-level handle draggable + move-button disabled
      for (const h of rowHandles) h.setDisabled(d);
      card.classList.toggle('fb-fieldset--disabled', d);
    },
    /** @param {boolean} up @param {boolean} down */
    setMoveState: reorder.setMoveState,
    /** @param {boolean} preferUp — re-home focus onto a fieldset Move button after a reorder rebuilt the card. */
    focusMove: reorder.focusMove,
    refreshValidity() {
      for (const h of rowHandles) h.refreshValidity();
    },
    destroy() {
      for (const h of rowHandles) h.destroy();
      rowHandles = [];
      reorder.destroy();
      card.remove();
    },
  };
}
